import React, { useCallback, useEffect, useState } from "react";
import { Col, Row, Toast, ToastContainer } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";

import Loader from "../common/Loader.jsx";
import DeleteModal from "../common/delete-modal.js";
import { useGlobal } from "../../GlobalContext";
import { canDelete, canEdit } from "../common/permissionUtils";
import {
  addRecordNoteApi,
  CUSTOM_MODULES,
  getRecordApi,
  getRecordHistoryApi,
  getRelatedRecordsApi,
  runRecordActionApi,
} from "./custom-module-api";
import { ACTION_COLORS, formatValue, isShown, listColumns, matches, recordTitle, settingsOf, singularOf } from "./custom-module-utils";
import { FieldValue, RecordDetails } from "./record-fields";
import Back from "../../assets/svg/back.svg";

const outlineButton = (color) => ({ border: `1px solid ${color}`, color, background: "#fff", borderRadius: "8px", padding: "8px 16px" });
const filledButton = (color) => ({ border: `1px solid ${color}`, color: "#fff", background: color, borderRadius: "8px", padding: "8px 16px" });

const HISTORY_STYLE = {
  created: { label: "Record created", color: "#17B65B" },
  updated: { label: "Record updated", color: "#3D4AF5" },
  action: { label: "Button used", color: "#D98E04" },
  note: { label: "Note", color: "#6929E2" },
};

// Records of other custom modules that point at this one through an
// autocomplete field, a few each, with a link to the full filtered list.
const RelatedRecords = ({ slug, id, title }) => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    (async () => {
      const response = await getRelatedRecordsApi(slug, id);
      if (response?.data?.success) setGroups(response.data.related ?? []);
    })();
  }, [slug, id]);

  return groups.map((group) => {
    const columns = listColumns(group.module).filter((field) => field.key !== group.field.key).slice(0, 3);
    return (
      <div key={`${group.module.slug}-${group.field.key}`} className="iv-card mb-3">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div className="iv-card-title mb-0">
            {group.module.name} <span className="fs-12 fw-500 text-grey">({group.total}) · by {group.field.label}</span>
          </div>
          <button
            type="button"
            className="btn btn-link p-0 fs-13"
            onClick={() =>
              navigate(`/custom/${group.module.slug}/list`, {
                state: { filters: { [group.field.key]: { value: Number(id), label: title } } },
              })
            }
          >
            View all
          </button>
        </div>
        {group.records.map((record) => (
          <div
            key={record.id}
            className="d-flex align-items-center gap-3 flex-wrap"
            style={{ borderTop: "1px solid #f1f2f4", padding: "10px 0", cursor: "pointer" }}
            onClick={() => navigate(`/custom/${group.module.slug}/view/${record.id}`)}
          >
            <div className="fs-14 fw-600 textblue" style={{ minWidth: "160px" }}>
              {record.code ? `${record.code} · ` : ""}{recordTitle(group.module, record)}
            </div>
            {columns.map((field) => (
              <div key={field.key} className="fs-13 fw-500 text-dkblue">
                <FieldValue field={field} value={record.data?.[field.key]} />
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  });
};

// Created / updated (with each field's before and after) / button used / notes.
const Timeline = ({ slug, id, module, refreshKey }) => {
  const [items, setItems] = useState([]);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const response = await getRecordHistoryApi(slug, id);
    if (response?.data?.success) setItems(response.data.history ?? []);
  }, [slug, id]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const addNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    setError("");
    const response = await addRecordNoteApi(slug, id, note.trim());
    setSaving(false);
    if (response?.data?.success) {
      setNote("");
      load();
    } else {
      setError(response?.data?.message ?? "Unable to add note");
    }
  };

  const fieldOf = (change) => module.fields.find((item) => item.key === change.key) ?? { type: "text" };
  const describe = (change) => `${change.label}: ${formatValue(fieldOf(change), change.from)} → ${formatValue(fieldOf(change), change.to)}`;
  // A hidden field's value stays off the page here too; the entry still says what happened.
  const shownChanges = (item) => (item.changes ?? []).filter((change) => isShown(fieldOf(change)));

  return (
    <div className="iv-card mb-3">
      <div className="iv-card-title">Activity</div>
      <label htmlFor="cm-note" className="visually-hidden">Add a note</label>
      <textarea id="cm-note" className="form-control mb-2" rows={2} value={note} maxLength={2000} placeholder="Add a note..." onChange={(e) => setNote(e.target.value)} />
      {error && <div className="fs-12 mb-2" style={{ color: "#ff4444" }}>{error}</div>}
      <div className="d-flex justify-content-end mb-3">
        <button type="button" className="btn-save" disabled={saving || !note.trim()} onClick={addNote}>
          {saving ? "Saving..." : "Add Note"}
        </button>
      </div>

      {items.length === 0 && <div className="fs-13 fw-500 text-grey">No activity yet.</div>}
      {items.map((item) => {
        const style = HISTORY_STYLE[item.type] ?? HISTORY_STYLE.updated;
        return (
          <div key={item.id} className="cm-timeline-item" style={{ "--cm-dot": style.color }}>
            <div className="fs-13 fw-600 text-dkblue">
              {item.type === "action" ? `"${item.note}" used` : style.label}
            </div>
            {item.type === "note" && <div className="fs-13 fw-500 text-dkblue cm-prewrap">{item.note}</div>}
            {shownChanges(item).map((change) => (
              <div key={change.key} className="fs-12 fw-500 text-grey">{describe(change)}</div>
            ))}
            <div className="fs-12 fw-500 text-grey">
              {item.created_by || "System"} · {item.created}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const CustomRecordView = () => {
  const navigate = useNavigate();
  const { slug, id } = useParams();

  const { rolePermissionList } = useGlobal();
  const hasEditPermission = canEdit(rolePermissionList, CUSTOM_MODULES);
  const hasDeletePermission = canDelete(rolePermissionList, CUSTOM_MODULES);

  const [module, setModule] = useState(null);
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);
  const [toast, setToast] = useState({ show: false, message: "", success: true });

  const showToast = (message, success) => setToast({ show: true, message, success });

  const fetchRecord = useCallback(async () => {
    try {
      const response = await getRecordApi(slug, id);
      if (response?.data?.success) {
        setModule(response.data.module);
        setRecord(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching record:", error);
    } finally {
      setLoading(false);
    }
  }, [slug, id]);

  useEffect(() => {
    setLoading(true);
    fetchRecord();
  }, [fetchRecord]);

  const runAction = async (action) => {
    if (action.confirm && !window.confirm(action.confirm)) return;
    setBusyAction(action.index);
    try {
      const response = await runRecordActionApi(slug, id, action.index);
      showToast(response?.data?.message ?? "Updated", response?.data?.success);
      if (response?.data?.success) {
        await fetchRecord();
        setHistoryKey((key) => key + 1);
      }
    } finally {
      setBusyAction(null);
    }
  };

  if (loading) return <Loader height="80vh" />;
  if (!module || !record) {
    return (
      <div className="fs-14 fw-500 text-grey text-center" style={{ padding: "80px 0" }}>
        This record or module does not exist.
      </div>
    );
  }

  const settings = settingsOf(module);
  const values = record.data ?? {};
  const title = recordTitle(module, record);
  // A button shows when its condition holds and it would actually change something.
  const actions = (module.actions ?? [])
    .map((action, index) => ({ ...action, index }))
    .filter(
      (action) =>
        values[action.field] !== action.value &&
        (!action.visible_when?.field || matches(values[action.visible_when.field], action.visible_when.value))
    );

  const detail = (label, value) => (
    <div key={label}>
      <div className="iv-detail-label">{label}</div>
      <div className="iv-detail-value">{value || "--"}</div>
    </div>
  );

  return (
    <div style={{ padding: "16px" }}>
      <ToastContainer position="top-end">
        <Toast bg={toast.success ? "success" : "danger"} show={toast.show} onClose={() => setToast({ ...toast, show: false })} delay={3000} autohide>
          <Toast.Body style={{ color: "#ffffff" }}>{toast.message}</Toast.Body>
        </Toast>
      </ToastContainer>

      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3">
        <div className="d-flex align-items-center gap-3">
          <img src={Back} alt="Back" style={{ cursor: "pointer" }} onClick={() => navigate(`/custom/${slug}/list`)} />
          <div>
            <div className="fs-20 fw-600 text-dkblue">{title}</div>
            <div className="fs-13 fw-500 text-grey">
              {record.code ? `${record.code} · ` : ""}{singularOf(module)}
            </div>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2 flex-wrap">
          {hasEditPermission &&
            actions.map((action) => (
              <button
                key={action.index}
                type="button"
                className="fs-14 fw-600"
                style={filledButton(ACTION_COLORS[action.color]?.color ?? ACTION_COLORS.primary.color)}
                disabled={busyAction !== null}
                onClick={() => runAction(action)}
              >
                {busyAction === action.index ? "Please wait..." : action.label}
              </button>
            ))}
          {hasDeletePermission && (
            <button type="button" className="fs-14 fw-600" style={outlineButton("#D61916")} onClick={() => setShowDelete(true)}>
              Delete
            </button>
          )}
          {hasEditPermission && (
            <button type="button" className="btn-save" onClick={() => navigate(`/custom/${slug}/edit/${id}`)}>
              Edit
            </button>
          )}
        </div>
      </div>

      <Row>
        <Col lg={8}>
          <RecordDetails module={module} values={values} />
          {settings.view.related && <RelatedRecords slug={slug} id={id} title={title} />}
        </Col>

        <Col lg={4}>
          <div className="iv-card mb-3">
            <div className="iv-card-title">Record Info</div>
            <div className="iv-detail-grid">
              {record.code && detail("Record No.", record.code)}
              {detail("Created By", record.created_by)}
              {detail("Created", record.created)}
            </div>
          </div>
          {settings.view.history && <Timeline slug={slug} id={id} module={module} refreshKey={historyKey} />}
        </Col>
      </Row>

      {showDelete && (
        <DeleteModal
          deleteUrl={`custom_module/${slug}/records/${id}`}
          onSuccess={(result) => {
            setShowDelete(false);
            if (result?.success) navigate(`/custom/${slug}/list`);
            else showToast(result?.message ?? "Unable to delete", false);
          }}
          onCancel={() => setShowDelete(false)}
        />
      )}
    </div>
  );
};

export default CustomRecordView;
